import { defineComponent, onMounted, reactive, SetupContext, computed, ref, toRefs, watch } from '@vue/composition-api';
import vueRouter from '@/router/index';
import CommonCategory from '@/components/common/common-category.component.vue';          // カテゴリ選択共通コンポーネント
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import RichTextEditor from '../common/rich-text-editor.component.vue';
import { AttachmentFile } from '@/shared/type';
import HttpStatus from 'http-status-codes';
import SystemConst from '@/shared/system-const';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                                      // Windowサイズ
    issueDivision: number;                                                     // 発出区分
    numberPrefix: string;                                                      // 発番接頭辞
    issueNumber: string;                                                       // 発出番号
    title: string;                                                             // タイトル
    issueDate: string;                                                         // 発出日
    body: string;                                                              // 本文
    organizationId: string;                                                    // 管理組織ID
    attachmentFiles: AttachmentFile[];                                         // 添付ファイル
    isAttachDisplay: boolean;                                                  // 添付ファイル表示切替
    isIntChangeCategory: boolean;                                              // 初期表示によるカテゴリの変更か判別
}

/**
 * 公文書詳細画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, RichTextEditor, CommonCategory },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                              // ルータ
        const docId = computed(() => ctx.root.$route.params.id).value;         // 公文書ID
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                                        // ウィンドウサイズ
            issueDivision: 0,                                                  // 発出区分
            numberPrefix: '',                                                  // 発番接頭辞
            issueNumber: '',                                                   // 発出番号
            title: '',                                                         // タイトル
            issueDate: '',                                                     // 発出日
            body: '',                                                          // 本文
            organizationId: '',                                                // 管理組織ID
            attachmentFiles: [],                                               // 添付ファイル情報
            isAttachDisplay: false,                                            // 添付ファイル表示切替
            isIntChangeCategory: false                                         // 初期表示によるカテゴリの変更か判別
        });
        const categoryRef = ref<any>(null);                                    // カテゴリー選択
        const commonRef = ref<any>(null);                                      // メッセージダイアログ
        const fileIconList = SystemConst.FILE_EXTENSION_ICON;                  // 添付ファイル拡張子アイコンリスト
        const detailHeight = SystemConst.DETAIL_HEIGHT.OFFICIAL_DOCUMENT       // 詳細画面高さ

        // カテゴリ選択のアクティブノードを監視する
        const { activeNode } = toRefs(ctx.root.$store.state.officialDocument);
        // カテゴリが選択された場合
        watch(activeNode, (newVal, oldVal) => {
            // 初期表示でない、かつホーム画面からの遷移でない場合
            if (oldVal.length !== 0 && !state.isIntChangeCategory) {
                // storeのアクティブノード以外の変数を初期化する
                ctx.root.$store.commit('officialDocument/clearStoreSelectCategory');

                // 公文書一覧（一般）画面へ遷移
                selectCategory();
            }

            // 初期表示によるカテゴリの変更フラグをfalseにする
            state.isIntChangeCategory = false;
        })

        /**
         * 掲載内容の設定
         * （リッチテキストエディタに表示するため、DOMの生成後のライフサイクルであるonMountedを使用）
         */
        onMounted(async () => {
            // vuexに認証ユーザー情報が存在しない場合
            if (ctx.root.$store.getters.getAuthenticationUser === null) {
                // URLから直接遷移してくる場合を想定し、認証情報を取得する
                await getAuthenticationUser();
            }

            // カテゴリデータの取得関数呼び出し
            await getCategory();

            // 初期表示データの取得関数呼び出し
            await getDetail();
        });

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-file-document-outline');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', '公文書詳細');
        }

        /**
         * 認証情報取得
         *
         * @return void
         */
        async function getAuthenticationUser() {
            // 認証情報取得
            const response = await ctx.root.$store.dispatch('samlAuthentication/getAuthenticationUser');

            // HTTPステータスコードが200以外
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示する
                ctx.root.$store.commit('setErrorMessage', response.data.message);
                vueRouter.push('/error');
                return;
            }
            // HTTPステータスコードが200
            // root.state.authenticationUserに認証ユーザー情報を設定する
            ctx.root.$store.commit('setAuthenticationUser', response.data.loginUser, { root: true });

            // root.state.themeColorに認証ユーザー情報で取得した基本色を設定する
            ctx.root.$store.commit('setThemeColor', response.data.loginUser.design.basicColor);
            // root.state.themeBackgroundに認証ユーザー情報で取得した背景色を設定する
            ctx.root.$store.commit('setThemeBackground', response.data.loginUser.design.backgroundColor);
            // root.state.topPagePathにトップページの情報を設定する
            ctx.root.$store.commit('setTopPagePath', '/home');
        }

        /**
         * カテゴリデータの取得
         */
        async function getCategory() {
            // カテゴリ取得
            // メールリンクからの遷移時など、アクティブなカテゴリが存在しない場合一覧（管理）画面に遷移してしまうため、
            // 以下メソッドの引数にフラグを追加して例外的に遷移しないようにする
            await categoryRef.value.getCategory(true);
        }

        /**
         * 初期表示データの取得
         */
        async function getDetail() {
            // 公文書IDをパラメータとして詳細データを取得する
            // GET /api/official-documents/detail/{officialDocumentId}
            // パラメータ：公文書ID
            const response = await ctx.root.$store.dispatch('officialDocument/getDocumentDetail',
                {
                    officialDocumentId: docId
                }
            );

            // API通信のレスポンスが正常に取得できなかった場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    // 400, 404エラーの場合、エラーメッセージを表示して公文書一覧（一般）へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/official-documents/list');
                    return;
                }
                // その他のエラーの場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // API通信のレスポンスが正常に取得できた場合、表示するデータを設定する
            state.issueDivision = response.data.documentDetail.issueDivision;
            state.numberPrefix = response.data.documentDetail.numberPrefix;
            state.issueNumber = response.data.documentDetail.issueNumber;
            state.title = response.data.documentDetail.title;
            state.issueDate = response.data.documentDetail.issueDate;

            // リッチテキストエディタに取得した内容を登録する
            state.body = response.data.documentDetail.body;

            // カテゴリ選択されている組織の確認用に保持
            state.organizationId = response.data.documentDetail.organizationId;

            // storeに保存されているカテゴリと公文書の管理組織が異なる場合
            if (ctx.root.$store.state.officialDocument.activeNode[0] !== state.organizationId) {
                // 初期表示によるカテゴリの変更フラグをtrueにする
                state.isIntChangeCategory = true;
                // 公文書の管理組織をカテゴリ選択し直す
                ctx.root.$store.commit('officialDocument/setActiveNode', [state.organizationId]);
                categoryRef.value.onActive();
            }

            // 添付ファイルデータを取得した場合、以下を添付ファイルの数分繰り返す
            const attachmentFilesNumber = response.data.documentDetail.attachmentFiles.length;
            if (attachmentFilesNumber !== 0) {
                for (let i = 0; i <= attachmentFilesNumber - 1; i++) {
                    const attachFile: AttachmentFile[] = [                  // 一時保存する添付ファイル情報
                        {
                            fileIcon: '',
                            fileSequence: 0,
                            fileName: '',
                            fileSize: ''
                        }
                    ];
                    attachFile[0].fileSequence = response.data.documentDetail.attachmentFiles[i].fileSequence;
                    attachFile[0].fileName = response.data.documentDetail.attachmentFiles[i].fileName;

                    // 添付ファイル名から拡張子部分を取得する
                    const extension = attachFile[0].fileName.substring(attachFile[0].fileName.lastIndexOf('.') + 1);
                    // アイコンのリストを作成する
                    const fileIcon = Object.keys(fileIconList);
                    // 添付ファイルアイコンを拡張子から判別し、設定する
                    Object.values(fileIconList).forEach((element, index) => {
                        // 添付ファイルの拡張子を小文字に変換して比較する
                        const iconList = element.some((extensionList) => extensionList === extension.toLowerCase());
                        if (iconList === true) {
                            // 拡張子を見つけたときの添え字番号で検索し、設定する
                            attachFile[0].fileIcon = fileIcon[index];
                        }
                        if (attachFile[0].fileIcon === '' && fileIcon.length - 1 === index) {
                            // 最後まで検索し、一致する拡張子がなかった場合
                            attachFile[0].fileIcon = fileIcon[index];
                        }
                    });

                    // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                    const fileSize = String(Math.round((response.data.documentDetail.attachmentFiles[i].fileSize / 1024) * 1000) / 1000);
                    attachFile[0].fileSize = fileSize;

                    state.attachmentFiles.push(attachFile[0]);
                }
                // 添付ファイルの表示切り替える
                state.isAttachDisplay = true;
            }
        }

        /**
         * 添付ファイルダウンロード処理
         * @param fileSequence ファイルシーケンス番号
         * @param fileName ダウンロードファイル名（拡張子有り）
         */
        async function fileDownload(fileSequence: number, fileName: string) {
            // 添付ファイルを取得するAPIを呼び出す
            // GET /api/official-documents/attachment-file
            // パラメータ：公文書ID
            //            ファイルシーケンス番号
            const response = await ctx.root.$store.dispatch('officialDocument/attachmentDownload',
                {
                    officialDocumentId: docId,
                    fileSequence: fileSequence,
                    fileName: fileName
                });

            // API通信成否判定
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
            }
            return;
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            // 一つ前の画面URLを取得する
            const lastTimePageUrl = document.referrer;

            // ポータルシステムの画面から遷移してきたか判断する
            if (window.history.length === 1 || lastTimePageUrl.indexOf(window.location.origin)) {
                // 一つ前の画面が存在しない場合、または前の画面がポータルシステムの画面でない場合は公文書一覧画面へ遷移する
                router.push('/official-documents/list');
            } else {
                // 基本的に前の画面へ戻る
                router.back();
            }
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
            // カテゴリー選択コンポーネントのサイズも変更
            categoryRef.value.resize(state.windowSize);
        }

        /**
         * カテゴリ選択時のイベント
         */
        function selectCategory() {
            // 公文書一覧（一般）画面へ遷移
            router.push('/official-documents/list');
        }

        return {
            state,
            categoryRef,
            commonRef,
            detailHeight,
            fileDownload,
            back,
            onResize
        };
    }
});
